import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Search, Plus, X } from "lucide-react";
import { deptAvatarClass } from "@/lib/employees";

const PICKER_WIDTH = 320;
const PICKER_MAX_HEIGHT = 384;

export default function EmployeePicker({ employees, selectedIds = [], onAdd, onClose, anchorLabel, anchorRef }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const popoverRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Position the portal popover (fixed) relative to the trigger, flipping up when needed.
  useLayoutEffect(() => {
    const el = anchorRef?.current;
    if (!el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      let left = r.left;
      if (left + PICKER_WIDTH > window.innerWidth - 8) left = window.innerWidth - PICKER_WIDTH - 8;
      if (left < 8) left = 8;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      if (spaceBelow >= 240 || spaceBelow >= spaceAbove) {
        setPos({ left, top: r.bottom + 4, maxHeight: Math.min(PICKER_MAX_HEIGHT, spaceBelow - 12) });
      } else {
        setPos({ left, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(PICKER_MAX_HEIGHT, spaceAbove - 12) });
      }
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    inputRef.current?.focus();
    const onClick = (e) => {
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      const inAnchor = anchorRef?.current && anchorRef.current.contains(e.target);
      if (!inPopover && !inAnchor) onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const t = setTimeout(() => document.addEventListener("mousedown", onClick), 100);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const selectedSet = new Set(selectedIds);
    return employees
      .filter((e) => !selectedSet.has(e.id))
      .filter((e) => {
        if (!ql) return true;
        return e.name.toLowerCase().includes(ql) ||
          e.role.toLowerCase().includes(ql) ||
          e.department.toLowerCase().includes(ql);
      })
      .sort((a, b) => a.name.localeCompare(b.name, "ro"))
      .slice(0, 30);
  }, [q, employees, selectedIds]);

  if (!pos) return null;

  const style = {
    position: "fixed",
    left: pos.left,
    width: PICKER_WIDTH,
    maxHeight: pos.maxHeight,
    ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }),
  };

  return createPortal(
    <div
      ref={popoverRef}
      data-testid="employee-picker"
      style={style}
      className="z-[200] flex flex-col bg-white dark:bg-zinc-900 border border-border rounded-lg shadow-xl overflow-hidden"
    >
      <div className="p-2 border-b border-border shrink-0">
        {anchorLabel && (
          <div className="px-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
            Adaugă în „{anchorLabel}"
          </div>
        )}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
          <input
            ref={inputRef}
            data-testid="picker-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Caută..."
            className="w-full h-8 pl-8 pr-7 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
          />
          <button
            data-testid="picker-close"
            onClick={onClose}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-zinc-400 text-center">Niciun rezultat.</div>
        ) : (
          filtered.map((e) => (
            <button
              key={e.id}
              data-testid={`picker-emp-${e.id}`}
              onClick={() => onAdd(e.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${deptAvatarClass(e.department)}`}>
                {e.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-zinc-900 dark:text-zinc-100 truncate">{e.name}</div>
                <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{e.role}</div>
              </div>
              <Plus className="h-3.5 w-3.5 text-zinc-400" />
            </button>
          ))
        )}
      </div>
    </div>,
    document.body
  );
}
